/**
 * Tech-aggregate summary helpers — shared between {@link HospitalityCard} and
 * {@link TeamPopover} for the unified appointment-detail surface (sketch 022
 * D-17).
 *
 * Order is locked to the trip lifecycle: scheduled → assigned → accepted →
 * traveling → arrived → departed. Zero-count buckets are omitted. The pieces
 * are exposed individually (TECH_STATUS_LABEL + TECH_STATUS_ORDER) so a caller
 * that needs a different join (e.g., a comma-separated tooltip) can still reuse
 * the canonical label table and ordering.
 *
 * Plan 04.6-08 lifted this out of HospitalityCard.vue + TeamPopover.vue —
 * the wave-1 plans inlined identical implementations because the intra-wave
 * constraint forbade touching this util while the wave was in flight.
 */

import type { AppointmentTechnician } from '../stores/useAppointmentsStore';

type TechStatus = AppointmentTechnician['technician_status'];

/**
 * Canonical label per status bucket — singular nouns/phrases that read
 * naturally as the right-hand side of a count (`2 en route` / `1 on site`).
 */
export const TECH_STATUS_LABEL: Readonly<Record<TechStatus, string>> = {
  scheduled: 'scheduled',
  assigned: 'assigned',
  accepted: 'accepted',
  traveling: 'en route',
  arrived: 'on site',
  departed: 'departed',
};

/**
 * Trip-lifecycle order: scheduled → assigned → accepted → traveling →
 * arrived → departed. `aggregateTechSummary` walks this list and emits the
 * non-zero buckets only, so a 4-bucket fixture reads as "1 scheduled · 1 en
 * route · 1 on site · 1 departed" while a 2-traveling fixture reads as "2 en
 * route" (no trailing middot).
 */
export const TECH_STATUS_ORDER: ReadonlyArray<TechStatus> = [
  'scheduled',
  'assigned',
  'accepted',
  'traveling',
  'arrived',
  'departed',
];

/**
 * Build the aggregate summary string for an arbitrary list of technicians.
 *
 * @example
 *   aggregateTechSummary([
 *     { technician_status: 'traveling' },
 *     { technician_status: 'traveling' },
 *     { technician_status: 'arrived' },
 *   ]) // '2 en route · 1 on site'
 *
 * @returns An empty string when the input is empty OR every bucket is zero
 *          (defensive — buckets only become zero with an unknown status,
 *          which the type system should already reject).
 */
export function aggregateTechSummary(
  technicians: ReadonlyArray<Pick<AppointmentTechnician, 'technician_status'>>,
): string {
  if (technicians.length === 0) return '';
  const counts: Record<TechStatus, number> = {
    scheduled: 0,
    assigned: 0,
    accepted: 0,
    traveling: 0,
    arrived: 0,
    departed: 0,
  };
  for (const tech of technicians) {
    counts[tech.technician_status] = (counts[tech.technician_status] ?? 0) + 1;
  }
  const parts: string[] = [];
  for (const key of TECH_STATUS_ORDER) {
    const n = counts[key];
    if (n > 0) parts.push(`${n} ${TECH_STATUS_LABEL[key]}`);
  }
  return parts.join(' · ');
}
